import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  IconButton,
  InputAdornment,
  Icon,
  useTheme,
  alpha,
  Tooltip,
  Drawer,
  Divider,
} from "@mui/material";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { toast } from "react-toastify";
import { facilitiesService } from "../../../services/facilities.service";
import { BranchFormDialog } from "./BranchFormDialog";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
  QuickFilter,
} from "../../../../../app.interface";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

export const BranchesManager: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [branches, setBranches] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [pageSize, setPageSize] = React.useState(25);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("name.keyword");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [totalItems, setTotalItems] = React.useState(0);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [selectedBranch, setSelectedBranch] = React.useState<IESDoc | null>(
    null
  );
  const [refresh, setRefresh] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedBranchDetails, setSelectedBranchDetails] =
    React.useState<IESDoc | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter branches by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: theme.palette.success.main,
          description: "Currently operating branches",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: theme.palette.error.main,
          description: "Temporarily closed or inactive branches",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort branches by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const quickFilters: QuickFilter[] = [
    {
      id: "active_branches",
      label: "Active Branches",
      icon: "store",
      color: "success",
      filters: { status: "active" },
    },
    {
      id: "inactive_branches",
      label: "Inactive Branches",
      icon: "store",
      color: "error",
      filters: { status: "inactive" },
    },
  ];

  const handleSort = (field: string) => {
    // Toggle sort order if clicking the same field
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Default to ascending when changing fields
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    // Find the original branch data that matches this row
    const branch = branches.find((b) => b._id === row._id);
    if (branch) {
      setSelectedBranchDetails(branch);
    }
  };

  const handleDrawerClose = () => {
    setSelectedBranchDetails(null);
  };

  const MasterColumns: TableColumn[] = [
    {
      id: "branchId",
      header: "Branch ID",
      field: "branchId",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        return (
          <Fragment>
            <Typography fontWeight={600}>
              {`B-${row.branchId.substring(0, 9).toUpperCase()}`}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      id: "branch_code",
      header: "Branch Code",
      field: "branch_code",
      type: "text",
      width: "180px",
    },
    {
      id: "name",
      header: "Branch Name",
      field: "name",
      filterField: "name.keyword",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "address",
      header: "Address",
      field: "address",
      type: "text",
      width: "300px",
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      filterField: "is_deleted",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: WithId & Record<string, any>) => (
        <div className="flex gap-2 justify-end">
          <Tooltip title="Edit Branch" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDialogOpen(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Branch" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleDialogOpen = (branch?: IESDoc) => {
    setSelectedBranch(branch || null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedBranch(null);
  };

  const handleSave = async (branchData: IESDoc) => {
    console.log("branchData", branchData);
    try {
      setSaving(true);
      if (selectedBranch?._id) {
        await facilitiesService.updateBranch({
          ...branchData,
          _id: selectedBranch._id,
        });
        toast.success("Branch updated successfully");
      } else {
        await facilitiesService.createBranch(branchData);
        toast.success("Branch created successfully");
      }
      handleDialogClose();
      loadBranches();
    } catch (error) {
      toast.error("Error saving branch");
      console.error("Error saving branch:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (branch: IESDoc) => {
    setSelectedBranch(branch);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedBranch(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedBranch) return;
    try {
      setSaving(true);
      await facilitiesService.deleteBranch(selectedBranch._id!);
      toast.success("Branch deleted successfully");
      handleCloseDeleteDialog();
      loadBranches();
    } catch (error) {
      toast.error("Failed to delete branch");
      console.error("Error deleting branch:", error);
    } finally {
      setSaving(false);
    }
  };

  const loadBranches = () => {
    setLoading(true);
    facilitiesService
      .listBranches({
        searchQuery: search,
        pageIndex,
        pageSize,
        ...filters,
        sortField: sortField,
        sortOrder: sortOrder,
      })
      .then((response) => {
        setBranches(response.data);
        setTotalItems(response.metadata.total.value);
      })
      .catch(() => {
        toast.error("Failed to load branches");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Load branches on mount and when search/pagination/filters change
  React.useEffect(loadBranches, [
    search,
    pageIndex,
    pageSize,
    refresh,
    filters,
    sortField,
    sortOrder,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Branch Management"
        subtitle="Manage your business branches and locations"
        icon="store"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleDialogOpen()}
            disabled={
              !hasPermission(
                "inventory.branches.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Branch
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search branches by name or address..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={branches.map((branch) => ({
            _id: branch._id,
            name: branch.name,
            address: branch.address,
            branchId: branch.branch_id,
            createdAt: branch.created_at,
            createdBy: branch.created_by_email,
            totalCost: branch.total_cost || 0,
            branch_code: branch?.branch_code || "-",
            status: branch.is_deleted ? "inactive" : "active",
          }))}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Branches List",
            subtitle: "Your Current list of branches",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => setRefresh((v) => !v),
            },
            export: {
              label: "Export Branches",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "branches-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: pageIndex,
            pageSize: pageSize,
            onPageChange: setPageIndex,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={handleRowClick}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      {/* Branch Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedBranchDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {selectedBranchDetails ? (
          <Fragment>
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Icon>store</Icon>
                <Typography variant="subtitle1" fontWeight={600}>
                  Branch Details
                </Typography>
              </Box>
              <IconButton
                onClick={handleDrawerClose}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box display="grid" gap={3}>
                <DetailItem
                  icon="store"
                  label="Branch Name"
                  value={selectedBranchDetails.name || "N/A"}
                />
                <DetailItem
                  icon="location_on"
                  label="Address"
                  value={selectedBranchDetails.address || "N/A"}
                />
                <DetailItem
                  icon="fiber_manual_record"
                  label="Status"
                  value={
                    selectedBranchDetails.is_deleted ? "Inactive" : "Active"
                  }
                  valueColor={
                    selectedBranchDetails.is_deleted ? "#DC2626" : "#059669"
                  }
                />
                <DetailItem
                  icon="schedule"
                  label="Created At"
                  value={
                    selectedBranchDetails.created_at
                      ? new Date(
                          selectedBranchDetails.created_at
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
                <DetailItem
                  icon="person"
                  label="Created By"
                  value={selectedBranchDetails.created_by_email || "N/A"}
                />
              </Box>

              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

              <Box display="flex" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    handleDialogOpen(selectedBranchDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.branches.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Branch
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    handleOpenDeleteDialog(selectedBranchDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.branches.delete",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Branch
                </Button>
              </Box>
            </Box>
          </Fragment>
        ) : null}
      </Drawer>

      <BranchFormDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        branch={selectedBranch}
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Branch"
        itemName={selectedBranch?.name || ""}
        itemDescription={selectedBranch?.address || ""}
        loading={saving}
        icon="store"
      />
    </Box>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);
